import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  AppBar,
  Toolbar,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Stack,
  IconButton,
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  Description as DescriptionIcon,
  AccountTree as TreeIcon,
  Edit as EditIcon,
  BusinessCenter as BusinessIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useSkillsCache } from '../context/skillsCacheContext';
import { api, API_ENDPOINTS } from '../config/api';

// ✅ Types matching the backend structures
interface SkillHeader {
  skillID: number;
  skillName: string;
  skillLinkID?: number;
}

interface SkillLink {
  skillLinkID: number;
  parentSkillID: number;
  parentName: string;
  offspringSkillID: number;
  offspringName: string;
  courseID: number;
}

interface SkillWithRelationships extends SkillHeader {
  parents: SkillHeader[];
  offspring: SkillHeader[];
}

export const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { logout, token } = useAuth();
  const { skills: cachedSkills, fetchSkills, loading: cacheLoading, getSkillHeaders } = useSkillsCache();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [skills, setSkills] = useState<SkillWithRelationships[]>([]);

  // Only fetch when token is available
  useEffect(() => {
    if (token) {
      loadDashboardData();
    }
  }, [token]);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch skills from cache
      await fetchSkills();

      // Get skill links
      const linksData = await api.get(API_ENDPOINTS.skills.heads);

      const links = linksData.links || [];

      // Map cached skills to SkillHeaders and process with links
      const skillHeaders = typeof getSkillHeaders === 'function' ? getSkillHeaders() : (cachedSkills || []).map(s => ({ skillID: s.skillID, skillName: s.skillName, skillLinkID: 0 }));
      const skillsWithRelationships = processSkillsAndLinks(skillHeaders, links);
      setSkills(skillsWithRelationships);
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
      setError('Failed to load skills data');
    } finally {
      setLoading(false);
    }
  };

  // ✅ NEW: Function to match skills with their parent/offspring relationships
  const processSkillsAndLinks = (
    skillHeaders: SkillHeader[],
    links: SkillLink[]
  ): SkillWithRelationships[] => {
    // Create a map for quick skill lookup by ID
    const skillMap = new Map<number, SkillHeader>();
    skillHeaders.forEach(skill => {
      skillMap.set(skill.skillID, skill);
    });

    // Initialize skills with empty relationship arrays
    const skillsWithRels: SkillWithRelationships[] = skillHeaders.map(skill => ({
      ...skill,
      parents: [],
      offspring: [],
    }));

    // Create a map for quick access to our processed skills
    const processedSkillMap = new Map<number, SkillWithRelationships>();
    skillsWithRels.forEach(skill => {
      processedSkillMap.set(skill.skillID, skill);
    });

    // ✅ Process links and populate parent/offspring relationships
    links.forEach(link => {
      const childSkill = processedSkillMap.get(link.offspringSkillID);
      const parentSkill = processedSkillMap.get(link.parentSkillID);

      if (childSkill && parentSkill) {
        // Add parent to child's parents array
        childSkill.parents.push({
          skillID: link.parentSkillID,
          skillName: link.parentName,
          skillLinkID: link.skillLinkID,
        });

        // Add child to parent's offspring array
        parentSkill.offspring.push({
          skillID: link.offspringSkillID,
          skillName: link.offspringName,
          skillLinkID: link.skillLinkID,
        });
      } else {
        console.warn('[Dashboard] Orphaned link:', link);
      }
    });

    // ✅ Sort by skill name for better UX
    return skillsWithRels.sort((a, b) => 
      a.skillName.localeCompare(b.skillName)
    );
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (loading || cacheLoading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        bgcolor: '#f5f5f5' 
      }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <AppBar position="static" sx={{ bgcolor: '#1976d2' }}>
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Skills Management Dashboard
          </Typography>
          <Button color="inherit" onClick={handleLogout}>
            Logout
          </Button>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Quick Actions */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <TreeIcon sx={{ fontSize: 40, color: '#1976d2', mr: 2 }} />
                  <Typography variant="h6">
                    Skills Tree
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  View and manage the hierarchical structure of skills and their relationships.
                </Typography>
              </CardContent>
              <CardActions>
                <Button 
                  size="small" 
                  onClick={() => navigate('/skills-tree')}
                  variant="contained"
                >
                  View Skills Tree
                </Button>
              </CardActions>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <DescriptionIcon sx={{ fontSize: 40, color: '#1976d2', mr: 2 }} />
                  <Typography variant="h6">
                    Manage Skills
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Create, edit, and delete skills. Define criteria and relationships.
                </Typography>
              </CardContent>
              <CardActions>
                <Button 
                  size="small" 
                  variant="contained"
                  onClick={() => navigate('/skill')}
                >
                  Manage Skills
                </Button>
              </CardActions>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ height: '100%', bgcolor: '#f8f9fa', border: '1px solid #dee2e6' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <BusinessIcon sx={{ fontSize: 40, color: '#388e3c', mr: 2 }} />
                  <Typography variant="h6">
                    Strategic Data Lake
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Non-school functions: Finance, HR, and Estates strategic integration.
                </Typography>
              </CardContent>
              <CardActions>
                <Button 
                  size="small" 
                  variant="outlined"
                  color="success"
                  onClick={() => navigate('/business-analytics')}
                >
                  View Strategic Insights
                </Button>
              </CardActions>
            </Card>
          </Grid>
        </Grid>

        {/* Skills Table with Relationships */}
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
            <Typography variant="h6">
              Skills Overview ({skills.length} skills)
            </Typography>
            <Typography variant="body2" color="text.secondary">
              All skills with their parent prerequisites and offspring outcomes
            </Typography>
          </Box>
          
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>
                    Skill Name
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>
                    Prerequisites (Parents)
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>
                    Leads To (Offspring)
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }} align="center">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {skills.map((skill) => (
                  <TableRow 
                    key={skill.skillID}
                    hover
                    sx={{ '&:hover': { bgcolor: '#f9f9f9' } }}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {skill.skillName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ID: {skill.skillID}
                      </Typography>
                    </TableCell>
                    
                    <TableCell>
                      {skill.parents.length > 0 ? (
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          {skill.parents.map((parent, index) => (
                            <Chip
                              key={`${parent.skillID}-${index}`}
                              label={parent.skillName}
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{ mb: 0.5 }}
                            />
                          ))}
                        </Stack>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          No prerequisites
                        </Typography>
                      )}
                    </TableCell>
                    
                    <TableCell>
                      {skill.offspring.length > 0 ? (
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          {skill.offspring.map((offspring, index) => (
                            <Chip
                              key={`${offspring.skillID}-${index}`}
                              label={offspring.skillName}
                              size="small"
                              color="secondary"
                              variant="outlined"
                              sx={{ mb: 0.5 }}
                            />
                          ))}
                        </Stack>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          No outcomes
                        </Typography>
                      )}
                    </TableCell>
                    
                    <TableCell align="center">
                      <IconButton 
                        size="small"
                        onClick={() => navigate(`/skill/${skill.skillID}`)}
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Container>
    </Box>
  );
};

export default Dashboard;